import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Heading } from '@/components/ui/heading';
import type { WritingStatus } from '../flowTypes';
import { JOURNAL_STEP_ICONS } from './stepIcons';
import { Loader2 } from 'lucide-react';

export type WritingStepProps = {
  draft: string;
  writingStatus: WritingStatus;
  commitment: string;
  alertTherapist: boolean;
  isAdvancing: boolean;
  onDraftChange: (value: string, isBlurred: boolean) => void;
  onCommitmentChange: (value: string, isBlurred: boolean) => void;
  onToggleAlertTherapist: (checked: boolean) => void;
  onAdvance: () => void;
};

const statusLabel: Record<WritingStatus, string> = {
  idle: 'Ultimo salvataggio automatico attivo',
  saving: 'Salvataggio...',
  saved: 'Bozza salvata',
};

export default function WritingStep({
  draft,
  writingStatus,
  commitment,
  alertTherapist,
  isAdvancing,
  onDraftChange,
  onCommitmentChange,
  onToggleAlertTherapist,
  onAdvance,
}: WritingStepProps) {
  const StepIcon = JOURNAL_STEP_ICONS.writing;
  const wordCount = draft.trim().split(/\s+/).filter(Boolean).length;

  return (
    <Card>
      <CardHeader>
        <Heading
          level={2}
          icon={StepIcon}
          description="Prenditi un momento di 2-3 minuti. Il testo viene salvato automaticamente ogni pochi secondi."
          wrapperClassName="mb-6"
        >
          Scrivi liberamente
        </Heading>
      </CardHeader>

      <CardContent className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value, false)}
          onBlur={(event) => onDraftChange(event.target.value, true)}
          placeholder="Racconta qui il tuo momento significativo..."
          rows={12}
          className="min-h-[280px]"
        />

        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{statusLabel[writingStatus]}</span>
          <span>{wordCount} parole</span>
        </div>

        <Separator />

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Impegno per domani (facoltativo)
            </label>
            <Input
              value={commitment}
              onChange={(event) => onCommitmentChange(event.target.value, false)}
              onBlur={(event) => onCommitmentChange(event.target.value, true)}
              placeholder="Domani mi prenderò 10 minuti per respirare..."
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="alert-therapist"
              checked={alertTherapist}
              onCheckedChange={(checked) => onToggleAlertTherapist(Boolean(checked))}
            />
            <label htmlFor="alert-therapist" className="text-sm text-muted-foreground">
              Avvisa lo psicologo che desideri un confronto
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button onClick={async () => await onAdvance()} disabled={isAdvancing}>
            {isAdvancing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Sto chiudendo...
              </span>
            ) : (
              'Chiudi il diario'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
